'use client';

import React, { Suspense, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { signOut, useSession } from 'next-auth/react';
import { useToast } from '@/hooks/useToast';
import { Loader2 } from 'lucide-react';
import {
  ButtonProviders,
  Providers,
  getAuthCallbackUrl,
} from '@/components/Auth/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import { logtail } from '@/utils/logtail';
import { getLoginRoute } from '@/lib/utils/app-routes';
import { perfectlyNineties } from '@/lib/fonts';
import posthog from '@/lib/posthog';
import { getFrontUrl } from '@/lib/auth/frontUrl';
import { fetchUserFromSession } from '@/apis/fetchUser';
import { fetchOrganizationByUUID } from '@/apis/fetchOrganization';
import { saveAuthInfo } from '@/apis/ApiUtil';
import { fetchOnboardingTracker } from '@/apis/onboardingTracker';
import AuthPayload from '@/types/authPayloadType';
import { AuthTestimonial } from '@/components/Auth/AuthTestimonial';
import { LazyMotion, m } from 'framer-motion';
import { domAnimation } from 'framer-motion';

const frontURL = getFrontUrl();

function SignUpProvidersContent({
  providers,
  isPopUp,
  callbackUrl,
}: {
  providers: Providers[];
  isPopUp: boolean;
  callbackUrl?: string;
}) {
  const toast = useToast();
  const [isLoading, setLoading] = useState(false);
  const router = useRouter();
  const params = useSearchParams();
  const [loading, extensionName, isCli] = [
    params.get('loading'),
    params.get('extensionName'),
    params.get('cli'),
  ];

  const { data: session } = useSession();
  const isRedirecting = useRef(false);

  const handleSignInClick = () => {
    if (extensionName) {
      router.push(`/login?extensionName=${extensionName}`);
    } else {
      router.push(getLoginRoute({ callbackURL: callbackUrl }));
    }
  };

  const redirect = async () => {
    if (isRedirecting.current) return;

    try {
      if (!session?.user?.provider) {
        return;
      }

      isRedirecting.current = true;

      const hasPendingOnboarding = async (params: {
        userUUID?: string;
        orgUUID?: string;
        gitHubUsername?: string;
        gitLabUsername?: string;
      }): Promise<boolean> => {
        if (!params.userUUID) {
          return false;
        }

        try {
          const trackerRes = await fetchOnboardingTracker({
            UserUUID: params.userUUID,
            OrgUUID: params.orgUUID || undefined,
            GitHubUsername: params.gitHubUsername || undefined,
            GitLabUsername: params.gitLabUsername || undefined,
          });

          if (trackerRes.Error || !trackerRes.Exists) {
            return false;
          }

          const step = (trackerRes.LastCompletedStep || '')
            .trim()
            .toLowerCase();
          return step.length > 0 && step !== 'completed';
        } catch {
          return false;
        }
      };

      const isCliFlow = localStorage.getItem('cli_signup_flow') === 'true';
      posthog.capture('signup_flow_started', {
        flow_type: extensionName ? 'extension' : isCliFlow ? 'cli' : 'standard',
        provider: session.user.provider,
        has_callback_url: !!callbackUrl,
      });

      if (extensionName) {
        router.push(`/extension/login?extensionName=${extensionName}`);
        posthog.capture('signup_flow_completed', {
          flow_type: 'extension',
          destination: 'extension_login',
          time_to_redirect: Date.now() - (window as any).signupStartTime,
        });
        return;
      }

      const userEmail = session.user.email;
      let existingUserHasOrganization = false;
      if (userEmail || (session.user as { id?: string }).id) {
        try {
          const existingUser = await fetchUserFromSession(session.user, {
            Authorization: `Bearer ${(session.user as any).accessToken}`,
          });

          const isCliFlow = localStorage.getItem('cli_signup_flow') === 'true';

          if (existingUser?.UserUUID && existingUser?.OrganizationUUID) {
            existingUserHasOrganization = true;
            const payloadSaveAuthInfo: AuthPayload = {
              AccessToken: (session.user as any).accessToken,
              RefreshToken: (session.user as any).refreshToken,
              IntegrationName: session.user.provider,
              UserUUID: existingUser.UserUUID,
            };

            if (session.user.provider === 'github') {
              payloadSaveAuthInfo.GithubUserName = (
                session.user as any
              ).ghUserName;
            } else if (session.user.provider === 'bitbucket') {
              payloadSaveAuthInfo.BitbucketUserName = session.user.username;
            } else {
              payloadSaveAuthInfo.GitlabUserName = (
                session.user as any
              ).glUserName;
            }

            await saveAuthInfo(payloadSaveAuthInfo);

            // Invite accept wins over wrapped-org / pending-onboarding resumes.
            const isJoinWorkspaceCallback =
              typeof callbackUrl === 'string' &&
              callbackUrl.startsWith('/join-workspace/');
            if (isJoinWorkspaceCallback) {
              router.push(callbackUrl);
              posthog.capture('signup_flow_completed', {
                flow_type: isCliFlow ? 'cli' : 'standard',
                destination: 'join_workspace_invite',
                has_organization: !!existingUser.OrganizationUUID,
                time_to_redirect: Date.now() - (window as any).signupStartTime,
              });
              setLoading(false);
              return;
            }

            // Wrapped-provisioned orgs already exist but still owe real onboarding.
            // The flag lives on the ORG; route the user into editable workspace-setup.
            // Skip in CLI flow (CLI users onboard later on web).
            if (!isCliFlow) {
              try {
                const orgRes = await fetchOrganizationByUUID(
                  existingUser.OrganizationUUID
                );
                const org = orgRes?.Org ?? orgRes;
                if (org?.RequiresOnboarding === true) {
                  router.push('/onboarding');
                  posthog.capture('signup_flow_completed', {
                    flow_type: 'standard',
                    destination: 'onboarding_org_requires_setup',
                    has_organization: true,
                    time_to_redirect:
                      Date.now() - (window as any).signupStartTime,
                  });
                  setLoading(false);
                  return;
                }
              } catch (orgErr) {
                // Non-fatal: fall through to the normal tracker-based decision.
                console.error('Error checking org onboarding flag:', orgErr);
              }
            }

            const shouldResumeOnboarding = await hasPendingOnboarding({
              userUUID: existingUser.UserUUID,
              orgUUID: existingUser.OrganizationUUID || undefined,
              gitHubUsername:
                existingUser.GithubUsername || (session.user as any).ghUserName,
              gitLabUsername:
                existingUser.GitlabUsername || (session.user as any).glUserName,
            });

            if (shouldResumeOnboarding) {
              router.push('/onboarding');
              posthog.capture('signup_flow_completed', {
                flow_type: isCliFlow ? 'cli' : 'standard',
                destination: 'onboarding_resume',
                has_organization: true,
                time_to_redirect: Date.now() - (window as any).signupStartTime,
              });
              setLoading(false);
              return;
            }

            if (isCliFlow) {
              localStorage.removeItem('cli_signup_flow');
              router.push('/settings?tab=api');
              posthog.capture('signup_flow_completed', {
                flow_type: 'cli',
                destination: 'settings_api',
                has_organization: true,
                time_to_redirect: Date.now() - (window as any).signupStartTime,
              });
              setLoading(false);
              return;
            }

            const redirectUrl = callbackUrl || '/home';
            router.push(redirectUrl);
            posthog.capture('signup_flow_completed', {
              flow_type: 'standard',
              destination: 'auto_signin_existing_user',
              has_organization: true,
              time_to_redirect: Date.now() - (window as any).signupStartTime,
            });
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error checking existing user:', error);
        }
      }

      if (isCliFlow && !existingUserHasOrganization) {
        router.push('/onboarding');
        posthog.capture('signup_flow_completed', {
          flow_type: 'cli',
          destination: 'onboarding_then_settings',
          time_to_redirect: Date.now() - (window as any).signupStartTime,
        });
      } else if (!existingUserHasOrganization) {
        // Invite flow: callbackUrl is /join-workspace/[uuid] — send user there to accept invite
        const isJoinWorkspaceCallback =
          typeof callbackUrl === 'string' &&
          callbackUrl.startsWith('/join-workspace/');
        const redirectUrl = isJoinWorkspaceCallback
          ? callbackUrl
          : '/onboarding';
        router.push(redirectUrl);
        posthog.capture('signup_flow_completed', {
          flow_type: 'standard',
          destination: isJoinWorkspaceCallback
            ? 'join_workspace_invite'
            : 'onboarding_new_user',
          time_to_redirect: Date.now() - (window as any).signupStartTime,
        });
      } else {
        router.push('/home');
        posthog.capture('signup_flow_completed', {
          flow_type: 'standard',
          destination: 'dashboard',
          time_to_redirect: Date.now() - (window as any).signupStartTime,
        });
      }

      setLoading(false);
    } catch (err) {
      isRedirecting.current = false;
      logtail.error(err + 'error in redirect', session?.user);
      logtail.flush();
      posthog.capture('signup_flow_error', {
        error_type: (err as Error).name || 'Unknown',
        error_message: (err as Error).message || 'An unknown error occurred',
        flow_type: extensionName ? 'extension' : 'standard',
        provider: session?.user?.provider,
        time_to_error: Date.now() - (window as any).signupStartTime,
      });
      toast({
        title: 'Error encountered while signing up',
        status: 'error',
        isClosable: true,
      });
      localStorage.clear();
      signOut({
        callbackUrl: `${frontURL}/signup${extensionName ? `?extensionName=${extensionName}` : ''}`,
      });
    }
  };

  useEffect(() => {
    if (!(window as any).signupStartTime) {
      (window as any).signupStartTime = Date.now();
    }
  }, []);

  useEffect(() => {
    redirect();
    // redirect is a plain function recreated every render; this effect is
    // intentionally keyed on session/router/params only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, router, params]);

  return (
    <LazyMotion features={domAnimation} strict>
      <m.div
        className={`flex flex-col md:flex-row ${isPopUp ? 'w-[350px]' : 'w-full'} h-full rounded-[12px] border border-ds-brand-darker shadow-[0px_12px_32px_0px_#1a1d14] overflow-hidden`}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Left panel */}
        <div className="flex flex-col flex-1 bg-ds-surface-off-white p-6">
          <div className="flex flex-col items-center justify-between flex-1 p-6 rounded-[12px]">
            {/* Logo */}
            <m.div
              className="shrink-0 self-center"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Image
                src="/assets/onboarding/Entelligence.svg"
                alt="Entelligence"
                width={162}
                height={24}
                className="h-6 w-auto opacity-40"
                style={{
                  filter:
                    'brightness(0) saturate(100%) invert(22%) sepia(17%) saturate(800%) hue-rotate(55deg) brightness(85%)',
                }}
                preload
              />
            </m.div>

            {/* Form area */}
            <div className="flex flex-col items-center gap-8 w-full max-w-[520px] mt-12">
              <m.div
                className="flex flex-col items-center text-center gap-3 w-full mb-14 -mt-6"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <h1
                  className={`${perfectlyNineties.className} text-[36px] font-medium text-ds-text-darker leading-[1.3] tracking-[0.72px] text-center w-full`}
                >
                  Get Started with Entelligence
                </h1>
                <p className="font-instrument-sans text-base font-medium text-ds-text-subtle leading-[1.45] text-center w-full">
                  Scale your engineering maturity with intelligent code reviews,
                  performance and contextual answers that lives where you work!
                </p>
              </m.div>

              <m.div
                className="flex flex-col gap-3 w-full"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.25,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {loading ? (
                  <div className="flex justify-center py-4">
                    <Loader2
                      className="h-6 w-6 animate-spin text-ds-text-subtle"
                      aria-hidden
                    />
                  </div>
                ) : (
                  <>
                    {providers.map((providerId) => (
                      <ButtonProviders
                        redirectUri={getAuthCallbackUrl(params, callbackUrl)}
                        provider={providerId}
                        isLoading={isLoading}
                        isSignIn={false}
                        key={providerId}
                      />
                    ))}
                  </>
                )}
              </m.div>

              {!isPopUp && (
                <m.p
                  className="font-instrument-sans text-xs font-medium text-ds-text-subtle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.35 }}
                >
                  <span>Already have an account? </span>
                  <button
                    type="button"
                    className="font-medium text-ds-brand-default underline hover:opacity-80 focus:outline-none"
                    onClick={handleSignInClick}
                  >
                    Sign-in
                  </button>
                </m.p>
              )}
            </div>

            <m.p
              className="font-instrument-sans text-xs font-medium text-ds-text-subtle text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              By signing up, you agree to our{' '}
              <a
                href="https://entelligence.ai/terms-and-condition"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ds-brand-subtle hover:underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="https://entelligence.ai/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ds-brand-subtle hover:underline"
              >
                Privacy Policy
              </a>
              .
            </m.p>
          </div>
        </div>

        <AuthTestimonial />
      </m.div>
    </LazyMotion>
  );
}

export const SignUpProviders = (props: {
  providers: Providers[];
  isPopUp: boolean;
  callbackUrl?: string;
}) => (
  <Suspense fallback={<div className="animate-pulse h-64" />}>
    <SignUpProvidersContent {...props} />
  </Suspense>
);
